import { useFrecRate } from "@frec-js/common-client";
import { classNames } from "@frec-js/common-web";
import { FC, Fragment } from "react";

import { useCompetitorRates } from "../hooks/useCompetitorRates";
import { useIsMobile } from "../hooks/useIsMobile";
import FrecLogo from "../public/webstatic/logo/frec.svg";

const formatter = Intl.NumberFormat("en", { notation: "compact" });

export const RateTable: FC = () => {
  const { data: frecRate } = useFrecRate();
  const { tieredExternalRates } = useCompetitorRates();
  return frecRate ? tieredExternalRates ? <TieredRateTable /> : null : null;
};

const TieredRateTable: FC = () => {
  const { data: frecRate } = useFrecRate();
  const { tieredExternalRates, updatedAt } = useCompetitorRates();
  const isMobile = useIsMobile();

  const tiers = tieredExternalRates?.reduce((acc, rate) => {
    rate.tiers.forEach((tier) => {
      if (!acc.includes(tier.tier)) {
        acc.push(tier.tier);
      }
    });
    return acc;
  }, [] as number[]);
  tiers?.sort((a, b) => a - b);

  return frecRate && tieredExternalRates ? (
    <>
      <div
        role="table"
        aria-label="Margin loan rates"
        className="dotted-h grid grid-flow-col grid-cols-[repeat(5,auto_1px)] grid-rows-6 border-y text-[12px] lg:grid-flow-row lg:grid-cols-6 lg:grid-rows-[repeat(5,100px_1px)] lg:text-[16px]"
      >
        <div className="dotted-v flex items-center bg-[#E9E5DD] px-2 lg:border-r lg:px-9">
          {/* empty */}
        </div>
        <div className="dotted-v bg-frecNeon flex items-center px-2 py-3 lg:justify-center lg:px-9">
          {isMobile ? (
            "Frec"
          ) : (
            <FrecLogo
              width="84"
              src="/webstatic/logo/frec.svg"
              aria-label="Frec"
            />
          )}
        </div>
        {tieredExternalRates.map((r, index) => {
          return (
            <div
              key={r.name}
              className={classNames(
                "dotted-v flex items-center px-2 lg:justify-center lg:bg-[#E9E5DD] lg:px-9 lg:text-center",
                index === 3 ? "" : "lg:border-r",
              )}
            >
              {r.name}
            </div>
          );
        })}
        {tiers?.map((tier) => (
          <Fragment key={tier}>
            <div className="dotted-h border-b">{/* empty */}</div>
            <div className="dotted-h bg-frecNeon border-b">{/* empty */}</div>
            <div className="dotted-h row-span-4 border-b lg:col-span-4 lg:row-auto">
              {/* empty */}
            </div>
            <div className="dotted-v flex items-center justify-center bg-[#E9E5DD] px-2 lg:border-r lg:bg-transparent lg:px-9">
              ${formatter.format(tier)}
            </div>
            <div className="dotted-v bg-frecNeon flex items-center justify-center px-2 lg:border-r lg:px-9">
              {frecRate.times(100).toFixed(2)}%
            </div>
            {tieredExternalRates.map((r, index) => {
              const rate = r.tiers.find((t) => t.tier === tier);
              return rate ? (
                <div
                  key={r.name}
                  className={classNames(
                    "dotted-v text-frecBlack/60 flex items-center justify-center px-2 lg:px-9",
                    index === 3 ? "" : "lg:border-r",
                  )}
                >
                  {rate.rate === "-" ? "Call" : `${rate.rate}%`}
                </div>
              ) : (
                <td></td>
              );
            })}
          </Fragment>
        ))}
      </div>
      <p className="text-frecDarkerBlack/50 text-12 mx-auto mt-8 max-w-xl text-center">
        Based on the US News & World Reports’ top four largest brokerage firms
        of 2024. All rates are as of {updatedAt}, the date of the last
        adjustment made to the EFFR by the Federal Reserve. Rates pulled from
        each competitor’s website and are subject to change without notice.
      </p>
    </>
  ) : null;
};
